import React from 'react';
import View from "react-native-web/dist/exports/View";
import PropTypes from 'prop-types';
import { viewports } from '@telus-uds/system-constants';
import { applyTextStyles, useTheme, useThemeTokens, useResponsiveThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, selectTokens, variantProp, viewProps, wrapStringsInText, createMediaQueryStyles, StyleSheet, useResponsiveProp } from '../utils';
import IconButton from '../IconButton';
import useCopy from '../utils/useCopy';
import dictionary from './dictionary';
import { useViewport } from '../ViewportProvider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CONTENT_MAX_WIDTH = 'max';
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectContainerStyles = tokens => ({
  ...tokens
});
const selectTextStyles = (tokens, themeOptions, isDismissible) => {
  const textTokens = selectTokens('Typography', tokens);
  const styles = {
    ...textTokens,
    themeOptions,
    overflow: 'hidden'
  };
  if (!isDismissible) {
    styles.flexShrink = 1;
  }
  return applyTextStyles(styles);
};
const selectIconProps = _ref => {
  let {
    iconSize,
    iconColor
  } = _ref;
  return {
    size: iconSize,
    color: iconColor
  };
};
const selectIconContainerStyles = _ref2 => {
  let {
    iconGap
  } = _ref2;
  return {
    paddingRight: iconGap,
    placeContent: 'start'
  };
};
const selectDismissIconProps = _ref3 => {
  let {
    dismissIconSize,
    dismissIconColor
  } = _ref3;
  return {
    size: dismissIconSize,
    color: dismissIconColor
  };
};
const selectDismissButtonContainerStyles = _ref4 => {
  let {
    dismissButtonGap
  } = _ref4;
  return {
    paddingLeft: dismissButtonGap,
    placeContent: 'start'
  };
};
const selectContentContainerStyle = (themeTokens, maxWidth, system, viewport, useContentMaxWidth) => ({
  maxWidth: system && (useContentMaxWidth || viewport === 'xl') ? maxWidth : '100%',
  width: '100%',
  paddingRight: themeTokens?.containerPaddingRight,
  paddingLeft: themeTokens?.containerPaddingLeft
});
const getMediaQueryStyles = (themeTokens, themeOptions, maxWidth, mediaIdsRef, dismissible, viewport, system, useContentMaxWidth) => {
  const transformedSelectContainerStyles = Object.entries(themeTokens).reduce((acc, _ref5) => {
    let [vp, viewportTokens] = _ref5;
    acc[vp] = {
      ...selectContainerStyles({
        ...viewportTokens
      }),
      flexDirection: 'column'
    };
    return acc;
  }, {});
  const selectContainerMediaQueryStyles = createMediaQueryStyles(transformedSelectContainerStyles);
  const {
    ids: containerIds,
    styles: containerStyles
  } = StyleSheet.create({
    container: {
      ...selectContainerMediaQueryStyles
    }
  });
  const transformedSelectContentContainerStyles = Object.entries(themeTokens).reduce((acc, _ref6) => {
    let [vp, viewportTokens] = _ref6;
    acc[vp] = {
      ...selectContentContainerStyle(viewportTokens, maxWidth, system, vp, useContentMaxWidth),
      flexDirection: 'row',
      flexShrink: 1,
      justifyContent: 'space-between',
      ...(system && {
        alignSelf: 'center'
      })
    };
    return acc;
  }, {});
  const selectContentContainerMediaQueryStyles = createMediaQueryStyles(transformedSelectContentContainerStyles);
  const {
    ids: contentContainerIds,
    styles: contentContainerStyles
  } = StyleSheet.create({
    contentContainer: {
      ...selectContentContainerMediaQueryStyles
    }
  });
  const {
    ids: staticContentContainerIds,
    styles: staticContentContainerStyles
  } = StyleSheet.create({
    staticContentContainer: {
      flexDirection: 'row',
      flexShrink: 1
    }
  });
  const {
    ids: iconContainerIds,
    styles: iconContainerStyles
  } = StyleSheet.create({
    iconContainer: selectIconContainerStyles(themeTokens[viewport])
  });
  const {
    ids: dismissButtonContainerIds,
    styles: dismissButtonContainerStyles
  } = StyleSheet.create({
    dismissButtonContainer: selectDismissButtonContainerStyles(themeTokens[viewport])
  });
  const {
    ids: textIds,
    styles: textStyles
  } = StyleSheet.create({
    text: selectTextStyles(themeTokens[viewport], themeOptions, dismissible)
  });
  const {
    styles: selectIconPropsStyles
  } = StyleSheet.create({
    selectIconProps: selectIconProps(themeTokens[viewport])
  });
  const {
    styles: selectDismissIconPropsStyles
  } = StyleSheet.create({
    selectDismissIconProps: selectDismissIconProps(themeTokens[viewport])
  });

  // eslint-disable-next-line no-param-reassign
  mediaIdsRef.current = {
    containerIds,
    contentContainerIds,
    staticContentContainerIds,
    iconContainerIds,
    dismissButtonContainerIds,
    textIds
  };
  return {
    containerStyles,
    contentContainerStyles,
    staticContentContainerStyles,
    iconContainerStyles,
    dismissButtonContainerStyles,
    textStyles,
    selectIconPropsStyles,
    selectDismissIconPropsStyles
  };
};
const getDefaultStyles = (themeTokens, themeOptions, maxWidth, dismissible, viewport, system, useContentMaxWidth) => ({
  containerStyles: {
    container: {
      flexDirection: 'column',
      ...selectContainerStyles(themeTokens)
    }
  },
  contentContainerStyles: {
    contentContainer: {
      flexDirection: 'row',
      flexShrink: 1,
      justifyContent: 'space-between',
      ...selectContentContainerStyle(themeTokens, maxWidth, system, viewport, useContentMaxWidth),
      ...(system && {
        alignSelf: 'center'
      })
    }
  },
  staticContentContainerStyles: {
    staticContentContainer: {
      flexDirection: 'row',
      flexShrink: 1
    }
  },
  iconContainerStyles: {
    iconContainer: {
      ...selectIconContainerStyles(themeTokens)
    }
  },
  dismissButtonContainerStyles: {
    dismissButtonContainer: {
      ...selectDismissButtonContainerStyles(themeTokens)
    }
  },
  textStyles: {
    text: {
      ...selectTextStyles(themeTokens, themeOptions, dismissible)
    }
  },
  selectIconPropsStyles: {
    selectIconProps: {
      ...selectIconProps(themeTokens)
    }
  },
  selectDismissIconPropsStyles: {
    selectDismissIconProps: {
      ...selectDismissIconProps(themeTokens)
    }
  }
});

/**
 * A banner that highlights important messages:
 * - Status message to show there is an error or outage of services
 * - Confirmation message in response to user action
 * - Account information letting the user know they have almost used all their data
 *
 * ## Usage criteria
 * - Use simple text content only, such as bold test or links. Don’t use other complex components or paragraphs
 * - Show notifications before the content that the message is related to, otherwise show at the top of the main content
 * - Show system notifications at the top of the page, below the navigation, and expands the full-width of the viewport
 * - When showing multiple notifications, show them in order of importance from top to bottom
 * - Use variants to visually show the type of message contained within the Notification.
 * - The icon and colour will indicate meaning and importance
 *
 * ### Variants
 * - Use `variant.style` to set the visual style of the notification
 * - Use `dismissible` prop to enable dismissible functionality
 * - Use `system` prop to set the visual style of the notification and denote an announcement from the system or application
 *
 * ### When to use the system prop?
 * - Use `system` to show system-based messages coming from the application
 * - Don’t use `system` when the message is in response to user action
 *
 * ## Variants
 *
 * #### Success
 * Use the success variant to provide feedback of a successful operation. The message will include an icon to indicate meaning and importance.
 *
 * #### Warning
 * Use the warning variant to provide feedback of a warning; users would still be able to proceed forward.
 * The message will include an icon to indicate meaning and importance.
 *
 * #### Error
 * Use the error variant to provide feedback of a failed operation. The message will include an icon to indicate meaning and importance.
 *
 * #### Dismissible
 * Dismissible notifications should only be used for default or success notifications where there is no action needed from the user.
 * If the information is passive then it should be dismissible as indicated by an interactive close button on the far right.
 * If the notification requires an action then make the option(s) clear with a `ChevronLink` or `Link`.
 *
 * Use the `dismissible` prop to allow users to dismiss the Notification at any time. Once dismissed, the Notification is removed from the DOM.
 *
 * Please note:
 *
 * - Default and Success variants are dismissible
 * - Error and Warning variants are not dismissible
 *
 * #### System message
 * Use full-width `Notifications` to show system-based messages coming from the application, not in response to user action.
 * Show system notifications at the top of the page, below the navigation, and expands the full-width of the viewport
 */
const Notification = /*#__PURE__*/React.forwardRef((_ref7, ref) => {
  let {
    children,
    system,
    dismissible,
    copy = 'en',
    tokens,
    variant,
    onDismiss,
    contentMinWidth,
    ...rest
  } = _ref7;
  const [isDismissed, setIsDismissed] = React.useState(false);
  const viewport = useViewport();
  const getCopy = useCopy({
    dictionary,
    copy
  });

  // TODO: Remove this once the system style variant is deprecated
  const isSystemEnabled = system || variant?.style?.includes('system');
  const {
    themeOptions
  } = useTheme();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? useResponsiveThemeTokens : useThemeTokens;
  const themeTokens = useTokens('Notification', tokens, variant, {
    system: isSystemEnabled,
    viewport
  });
  const useContentMaxWidth = useResponsiveProp(contentMinWidth) === CONTENT_MAX_WIDTH;
  const maxWidth = useResponsiveProp(themeOptions?.contentMaxWidth, viewports.map.get(viewports.xl));
  const notificationComponentRef = React.useRef({
    containerStyles: {},
    contentContainerStyles: {},
    staticContentContainerStyles: {},
    iconContainerStyles: {},
    dismissButtonContainerStyles: {},
    textStyles: {},
    selectIconPropsStyles: {},
    selectDismissIconPropsStyles: {}
  });
  const mediaIdsRef = React.useRef({
    containerIds: {},
    contentContainerIds: {},
    staticContentContainerIds: {},
    iconContainerIds: {},
    dismissButtonContainerIds: {},
    textIds: {},
    selectIconPropsIds: {},
    selectDismissIconPropsIds: {}
  });
  if (enableMediaQueryStyleSheet) {
    notificationComponentRef.current = getMediaQueryStyles(themeTokens, themeOptions, maxWidth, mediaIdsRef, dismissible, viewport, isSystemEnabled, useContentMaxWidth);
  } else {
    notificationComponentRef.current = getDefaultStyles(themeTokens, themeOptions, maxWidth, dismissible, viewport, isSystemEnabled, useContentMaxWidth);
  }
  if (isDismissed) {
    return null;
  }
  const content = wrapStringsInText(typeof children === 'function' ? children({
    textStyles: notificationComponentRef.current.textStyles.text,
    variant
  }) : children, {
    style: notificationComponentRef.current.textStyles.text
  });
  const {
    icon: IconComponent,
    dismissIcon: DismissIconComponent,
    dismissIconColor
  } = enableMediaQueryStyleSheet === false ? themeTokens : themeTokens[viewport];
  const onDismissPress = () => {
    setIsDismissed(true);
    onDismiss?.();
  };
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: notificationComponentRef.current.containerStyles.container,
    dataSet: mediaIdsRef && {
      media: mediaIdsRef.current.containerIds.container
    },
    ...selectProps(rest),
    children: /*#__PURE__*/_jsxs(View, {
      style: notificationComponentRef.current.contentContainerStyles.contentContainer,
      dataSet: mediaIdsRef && {
        media: mediaIdsRef.current.contentContainerIds.contentContainer
      },
      children: [/*#__PURE__*/_jsxs(View, {
        style: notificationComponentRef.current.staticContentContainerStyles.staticContentContainer,
        dataSet: mediaIdsRef && {
          media: mediaIdsRef.current.staticContentContainerIds.staticContentContainer
        },
        children: [IconComponent && /*#__PURE__*/_jsx(View, {
          style: notificationComponentRef.current.iconContainerStyles.iconContainer,
          dataSet: mediaIdsRef && {
            media: mediaIdsRef.current.iconContainerIds.iconContainer
          },
          children: /*#__PURE__*/_jsx(IconComponent, {
            ...notificationComponentRef.current.selectIconPropsStyles.selectIconProps
          })
        }), content && typeof content === 'function' ? content({
          textStyles: notificationComponentRef.current.textStyles.text,
          variant
        }) : content]
      }), dismissible && DismissIconComponent && /*#__PURE__*/_jsx(View, {
        style: notificationComponentRef.current.dismissButtonContainerStyles.dismissButtonContainer,
        dataSet: mediaIdsRef && {
          media: mediaIdsRef.current.dismissButtonContainerIds.dismissButtonContainer
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          action: "close",
          onPress: onDismissPress,
          icon: DismissIconComponent,
          accessibilityRole: "button",
          accessibilityLabel: getCopy('dismiss'),
          variant: {
            inverse: dismissIconColor === '#ffffff',
            size: 'small'
          },
          children: () => /*#__PURE__*/_jsx(DismissIconComponent, {
            ...notificationComponentRef.current.selectDismissIconPropsStyles.selectDismissIconProps
          })
        })
      })]
    })
  });
});
Notification.displayName = 'Notification';
Notification.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Content of the `Notification`.
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
  /**
   * Use `system` prop to set the visual style of the notification and denote an announcement from the system or application
   */
  system: PropTypes.bool,
  /**
   * Use the `dismissible` prop to allow users to dismiss the Notification at any time.
   */
  dismissible: PropTypes.bool,
  /**
   * Select English or French copy for the accessible label of the dismiss button.
   */
  copy: PropTypes.oneOfType([PropTypes.oneOf(['en', 'fr']), PropTypes.shape({
    dismiss: PropTypes.string
  })]),
  /**
   * Callback function called when the dismiss button is clicked
   */
  onDismiss: PropTypes.func,
  /**
   * The minimum width of the content in the Notification when using the system variant.
   * This prop accepts responsive values for different viewports.
   * - `xs`: 'max' | 'full'
   * - `sm`: 'max' | 'full'
   * - `md`: 'max' | 'full'
   * - `lg`: 'max' | 'full'
   * - `xl`: 'max' | 'full'
   */
  contentMinWidth: PropTypes.shape({
    xl: PropTypes.oneOf(['max', 'full']),
    lg: PropTypes.oneOf(['max', 'full']),
    md: PropTypes.oneOf(['max', 'full']),
    sm: PropTypes.oneOf(['max', 'full']),
    xs: PropTypes.oneOf(['max', 'full'])
  }),
  tokens: getTokensPropType('Notification'),
  variant: variantProp.propType
};
export default Notification;